import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useTranslation } from "react-i18next"
import { useIndexProps } from "./page-props"
import { PageBody } from "@/components/ui/layout"
import {
  ArrowDownUp,
  BellElectric,
  CalendarClock,
  Inbox,
  Pencil,
  PlusCircle,
  Trash,
  Users,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card"
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  Empty,
} from "@/components/ui/empty"
import { EducatorsCustomClassesClassIndex } from "@/types/serializers"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { ListingCard } from "@/components/shared/listing-card"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["classes", "common"])
  const { classes } = useIndexProps()

  return (
    <>
      <EducatorPageChrome
        action={
          <ActionLink variant="solid" href="./classes/new" Icon={PlusCircle}>
            {t("classes:index.create")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <ul className="flex flex-col gap-6">
          {classes.length === 0 && (
            <Card>
              <CardContent>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>
                      {t("classes:index.empty_list_title")}
                    </EmptyTitle>
                    <EmptyDescription>
                      {t("classes:index.empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Link href="./classes/new" as={Button}>
                      <PlusCircle />
                      {t("classes:index.create")}
                    </Link>
                  </EmptyContent>
                </Empty>
              </CardContent>
            </Card>
          )}
          {classes.map(({ listing_profile, collection }) => (
            <li key={listing_profile.id} className="contents">
              <ListingCard listing={listing_profile}>
                {collection.map((item) => (
                  <li key={item.id}>
                    <ClassCard customClass={item} />
                  </li>
                ))}
              </ListingCard>
            </li>
          ))}
        </ul>
      </PageBody>
    </>
  )
}

function ClassCard({
  customClass,
}: {
  customClass: EducatorsCustomClassesClassIndex
}) {
  const { t } = useTranslation(["classes", "common"])
  return (
    <Card data-testid="class-listing-card">
      <CardHeader className="flex flex-row items-center justify-between align-bottom">
        <div>
          <CardTitle>{customClass.name}</CardTitle>
          <div data-testid="num_classes">
            {t("classes:index.num_offered", {
              count: customClass.num_classes,
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription>
          <dl className="grid w-fit grid-cols-2 gap-2">
            <dt>{t("common:models.state")}</dt>
            <dd className="font-semibold" data-testid="state">
              {customClass.state_name}
            </dd>
            <dt>{t("common:models.subject")}</dt>
            <dd className="font-semibold" data-testid="subject">
              {customClass.subject_name}
            </dd>
            <dt>{t("common:models.speciality")}</dt>
            <dd data-testid="specialities">
              {customClass.specialities.join(", ")}
            </dd>
            <dt>{t("classes:index.availability")}</dt>
            <dd data-testid="availability">
              {t("classes:index.hours_per_week", {
                hours: customClass.availability_hours.toLocaleString(
                  undefined,
                  {
                    maximumFractionDigits: 2,
                  },
                ),
              })}
            </dd>
          </dl>
        </CardDescription>
      </CardContent>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        <Link href={`./classes/${customClass.id}/edit`} as={Button}>
          <Pencil />
          {t("common:actions.edit")}
        </Link>
        <Link
          href={`./classes/${customClass.id}/teacher_class_listing_availability/edit`}
          as={Button}
        >
          <CalendarClock />
          {t("classes:index.set_availability")}
        </Link>
        <Link href={`./classes/${customClass.id}/go_live`} as={Button}>
          <BellElectric />
          {t("classes:index.go_live")}
        </Link>
        <Link href={`./classes/${customClass.id}/students`} as={Button}>
          <Users />
          {t("classes:index.students")}
        </Link>
        <Link href={`./classes/${customClass.id}/transfers`} as={Button}>
          <ArrowDownUp />
          {t("classes:index.transfers")}
        </Link>
        <ConfirmationDialog
          action={
            <Link
              className={buttonVariants({ variant: "destructive" })}
              href={`./classes/${customClass.id}`}
              method="delete"
              as={Button}
            >
              {t("common:actions.confirm")}
            </Link>
          }
        >
          <ConfirmationDialogTrigger asChild>
            <Button variant={"destructive-outline"}>
              <Trash />
              {t("common:actions.delete")}
            </Button>
          </ConfirmationDialogTrigger>
        </ConfirmationDialog>
      </CardFooter>
    </Card>
  )
}
